import { Controller } from "@hotwired/stimulus"
import { createI18nGetter } from "../lib/i18n_helper"

// flatpickr is imported lazily in initializeFlatpickr() so the library stays out of the eager
// application.js entry — it only loads on form pages that actually use a datetime picker.

// Flatpickr Controller
// Enhanced datetime picker with flatpickr library
//
// Features:
// - 30-minute increment time selection (minuteIncrement: 30)
// - Respects server-provided min/max attributes (SSOT principle)
// - Min/max date validation
// - Localized to Traditional Chinese (zh-TW)
// - Mobile-friendly interface with disableMobile option
//
// Usage:
// <input type="datetime-local"
//   data-controller="flatpickr"
//   data-flatpickr-options-value='{"enableTime": true, "minDate": "today"}'
//   data-i18n-past-date="請選擇未來的日期時間"
//   data-i18n-business-hours="請選擇營業時間 (09:00-21:00)">
export default class extends Controller {
  static values = {
    options: { type: Object, default: {} }
  }

  connect() {
    this.disconnected = false
    this.input = this.element
    this.i18n = createI18nGetter(this)

    // Initialize flatpickr with proper configuration
    this.initializeFlatpickr()
  }

  disconnect() {
    this.disconnected = true
    if (this.picker) {
      this.picker.destroy()
    }
  }

  // Initialize flatpickr datetime picker (library loaded lazily)
  async initializeFlatpickr() {
    const config = this.flatpickrConfig()

    try {
      const { default: flatpickr } = await import("flatpickr")
      // The controller may have disconnected while flatpickr was loading.
      if (this.disconnected) return
      this.picker = flatpickr(this.input, config)
    } catch (error) {
      console.error('Failed to initialize flatpickr:', error)
      // Fallback to native input if flatpickr fails (or the dynamic import fails)
      this.setupNativeInput()
    }
  }

  // Setup native datetime-local input
  setupNativeInput() {
    const options = this.optionsValue

    // Set minimum date (only if server hasn't already set it - SSOT principle)
    if (options.minDate && !this.input.min) {
      const minDate = this.parseDate(options.minDate)
      if (minDate) {
        this.input.min = this.formatDatetimeLocal(minDate)
      }
    }

    // Set maximum date (only if server hasn't already set it - SSOT principle)
    if (options.maxDate && !this.input.max) {
      const maxDate = this.parseDate(options.maxDate)
      if (maxDate) {
        this.input.max = this.formatDatetimeLocal(maxDate)
      }
    }

    // Set step for time (default: 30 minutes)
    if (options.enableTime && !this.input.step) {
      this.input.step = options.minuteIncrement ? options.minuteIncrement * 60 : 1800
    }

    // Validate on change
    this.input.addEventListener("change", this.validate.bind(this))
  }

  // Validate datetime input
  validate(event) {
    const value = this.input.value
    if (!value) return

    const selectedDate = new Date(value)
    const now = new Date()
    const options = this.optionsValue

    // Check minimum date
    if (options.minDate === "today" && selectedDate < now) {
      this.showError(this.i18n('PastDate', '請選擇未來的日期時間'))
      this.input.value = ""
      return
    }

    // Check business hours (optional)
    if (options.businessHours) {
      const hour = selectedDate.getHours()
      if (hour < 9 || hour >= 21) {
        this.showError(this.i18n('BusinessHours', '請選擇營業時間 (09:00-21:00)'))
        this.input.value = ""
        return
      }
    }
  }

  // Show validation error
  showError(message) {
    // Find or create error message element
    let errorElement = this.input.parentElement.querySelector(".error-message")

    if (!errorElement) {
      errorElement = document.createElement("p")
      errorElement.className = "error-message text-sm text-red-600 mt-1"
      this.input.parentElement.appendChild(errorElement)
    }

    errorElement.textContent = message
    this.input.classList.add("border-red-500")

    // Clear error after duration (BusinessConstants::UITiming::TOAST_DURATION_MS)
    setTimeout(() => {
      errorElement.textContent = ""
      this.input.classList.remove("border-red-500")
    }, window.BusinessConstants.UITiming.TOAST_DURATION_MS)
  }

  // Parse date string (supports "today", ISO strings, Date objects)
  parseDate(dateValue) {
    if (dateValue === "today") {
      return new Date()
    }

    if (dateValue instanceof Date) {
      return dateValue
    }

    if (typeof dateValue === "string") {
      return new Date(dateValue)
    }

    return null
  }

  // Format date for datetime-local input (YYYY-MM-DDTHH:MM)
  formatDatetimeLocal(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")

    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Flatpickr configuration with server SSOT respect
  flatpickrConfig() {
    const options = this.optionsValue

    // Extract server-provided min/max from HTML attributes (SSOT principle)
    const serverMin = this.input.getAttribute('min')
    const serverMax = this.input.getAttribute('max')
    const serverValue = this.input.value

    // Priority: Server attributes > options > defaults
    const minDate = serverMin || options.minDate || "today"
    const maxDate = serverMax || options.maxDate || null
    const defaultDate = serverValue || minDate

    return {
      enableTime: true,
      time_24hr: false,  // 12-hour format for Traditional Chinese users
      dateFormat: "Y-m-d H:i",
      minDate,
      maxDate,
      defaultDate,
      minuteIncrement: 30,  // ⭐ 30-minute grid (core fix)
      allowInput: true,     // Allow manual typing
      disableMobile: false, // Use native picker on mobile for better UX
      locale: this.getFlatpickrLocale(),
      // Prevent flatpickr from modifying server min/max
      onReady: (selectedDates, dateStr, instance) => {
        // Ensure server min/max are preserved
        if (serverMin) {
          instance.set('minDate', serverMin)
        }
        if (serverMax) {
          instance.set('maxDate', serverMax)
        }
      },
      ...options
    }
  }

  // Get flatpickr locale from I18n
  // Loads locale configuration from data-i18n-locale-* attributes
  getFlatpickrLocale() {
    const weekdaysShort = this.element.dataset.i18nLocaleWeekdaysShort
    const weekdaysLong = this.element.dataset.i18nLocaleWeekdaysLong
    const monthsShort = this.element.dataset.i18nLocaleMonthsShort
    const monthsLong = this.element.dataset.i18nLocaleMonthsLong
    const firstDayOfWeek = this.element.dataset.i18nLocaleFirstDayOfWeek

    if (weekdaysShort && weekdaysLong && monthsShort && monthsLong) {
      return {
        firstDayOfWeek: firstDayOfWeek ? parseInt(firstDayOfWeek, 10) : 1,
        weekdays: {
          shorthand: JSON.parse(weekdaysShort),
          longhand: JSON.parse(weekdaysLong)
        },
        months: {
          shorthand: JSON.parse(monthsShort),
          longhand: JSON.parse(monthsLong)
        }
      }
    }

    // No hardcoded fallback - locale must be provided via I18n
    console.warn('[Flatpickr] Locale data attributes not found. Check ERB template I18n configuration.')
    return null
  }
}
